import type { Store } from './store';
import { notifyError, notifySuccess } from '../services/notifications';
import Routes from '../routes';
import { Message } from './tree';

export async function nodeDispatch(store: Store, msg: Message): Promise<void> {
  const { payload } = msg;

  if (msg.target) {
    switch (msg.target) {
      case 'Notifications':
      case 'NotificationService':
        store.notificationService.invoke(msg.type, msg.payload);
        break;

      case 'Overlays':
      case 'OverlayService':
        store.overlayService.invoke(msg.type, msg.payload);
        break;

      case 'Routing':
      case 'RoutingService':
        store.routingService.invoke(msg.type, msg.payload);
        break;
    }
    return;
  }

  switch (msg.type) {

    case 'deleteBookmark': {
      const bkm = store.getBookmark(msg.payload.bookmarkId);

      const [, err] = await store.apiDeleteBookmark({
        bookmarkId: bkm.id,
        listId: bkm.listId
      });

      if (err)
        return notifyError(msg.sender, 'Could not remove bookmark.');
      notifySuccess(msg.sender, 'Bookmark removed.');
      await store.apiFetchBookmarkList(bkm.listId);

    } break;

    case 'openBookmarkInUserPlayer': {
      const bkmId = payload.bookmarkId;
      const bkm = store.maybeGetBookmark(bkmId);
      if (!bkm || !bkm.jobId)
        break;

      let query: string | undefined;
      if (bkm.momentId)
        query = `momentId=${bkm.momentId}`;

      store.goTo(Routes.userVideo(bkm.jobId, query));
    } break;

    case 'openBookmarkWindow':
      store.bookmarkWindow.openBookmark(payload.bookmarkId);
      break;

    case 'openEditBookmarkListWindow':
      store.bookmarkListWindow.openEdit(payload.bookmarkListId);
      break;

    case 'openDeleteBookmarkListWindow':
      store.deleteBookmarkListWindow.open({
        bookmarkListId: payload.bookmarkListId,
        shouldRedirect: payload.shouldRedirect
      });
      break;

    case 'openShareVideoWindow':
      store.shareVideoWindow.open({
        bookmark: payload.bookmark,
        mode: payload.mode,
        sharedFromLocation: payload.sharedFromLocation,
        job: payload.job,
        teamId: payload.teamId,
        moment: payload.moment
      });
      break;

    case 'openCopyJobWindow':
      store.copyJobWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId
      });
      break;

    case 'openAddJobToTeamWindow':
      store.addJobToTeamWindow.open({
        jobId: payload.jobId
      });
      break;

    case 'openRemoveJobFromTeamWindow':
      store.removeJobFromTeamWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId
      });
      break;

    case 'openDeleteJobWindow':
      store.deleteJobWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId
      });
      break;

    case 'openRetryJobWindow':
      store.retryJobWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId,
        sourceType: payload.sourceType,
      });
      break;

    case 'openDownloadJobWindow':
      store.downloadJobWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId,
        startTime: payload.startTime,
        endTime: payload.endTime
      });
      break;

    case 'openExportToEdlWindow':
      store.exportToEdlWindow.open({
        bookmarkListId: payload.bookmarkListId
      });
      break;

    case 'openDownloadTranscriptWindow':
      store.downloadWindow.open({
        message: payload.message,
        onSubmit: payload.onSubmit,
        formatItems: payload.formatItems,
        title: payload.title
      });
      break;

    case 'openDownloadJobSlidesWindow':
      store.downloadJobSlidesWindow.open({
        jobId: payload.jobId
      });
      break;

    case 'openDeleteStreamWindow':
      store.deleteStreamWindow.open({
        streamId: payload.streamId,
        streamName: payload.streamName
      });
      break;

    case 'openRemoveJobFromCliprWindow':
      store.removeJobFromCliprWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId
      });
      break;

    case 'openIngestFromExternalLibraryWindow':
      store.ingestFromExternalLibraryWindow.open(payload);
      break;

    case 'openRenameJobWindow':
      store.renameJobWindow.open({
        page: payload.page,
        jobId: payload.jobId,
        title: payload.title,
        isDocumentTranslation: payload.isDocumentTranslation,
      });
      break;

    case 'openChangeJobOwnerWindow':
      store.changeJobOwnerWindow.open({
        jobId: payload.jobId
      });
      break;

    case 'openPostRegistrationTutorial': {
      store.postRegistrationWindow.open();
      break;
    }

    case 'openAccountGuidedTraining':
      store.accountGuidedTraining.open({
        showUploadButtons: payload.showUploadButtons,
        showTrainButton: payload.showTrainButton,
        teamId: payload.teamId,
      });
      break;

    case 'openPlayerTutorialWindow':
      store.playerTutorialWindow.open({
        steps: payload.steps,
        pageNumber: payload.pageNumber
      });
      break;

    case 'openPlayerTutorialHelpButtonTooltip':
      store.playerTutorialHelpButtonTooltip.open();
      break;

    case 'openPlayerTutorialConfirmationWindow':
      store.playerTutorialConfirmationWindowState.open({
        steps: payload.steps
      });
      break;

    case 'openPlayerTutorialTopicsWindow':
      store.playerTutorialTopicsWindow.open({
        steps: payload.steps,
      });
      break;

    case 'openDeleteTrackPopup':
      store.deleteTrack.open({
        job: payload.job,
        jobId: payload.jobId,
        trackId: payload.trackId
      });
      break;

    case 'openLeaveTeamWindow':
      store.leaveTeamWindow.open({
        teamId: payload.teamId
      });
      break;

    case 'openDeleteTeamWindow':
      store.deleteTeamWindow.open({
        teamId: payload.teamId
      });
      break;

    case 'openToggleCliprGptWindow':
      store.toggleCliprGptWindow.open({
        teamId: payload.teamId,
        provider: payload.provider,
        summarizationEnabled: payload.summarizationEnabled,
        actionItemsExtractionEnabled: payload.actionItemsExtractionEnabled,
        cliprGptEnabled: payload.cliprGptEnabled,
        description: payload.description,
        button: payload.button,
        header: payload.header
      });
      break;

    case 'openTogglePublicSafetyWindow':
      store.togglePublicSafetyWindow.open({
        teamId: payload.teamId,
        publicSafety: payload.publicSafety
      });
      break;

    case 'openDeceptioAiWindow':
      store.toggleDeceptioAiWindow.open({
        teamId: payload.teamId,
        deceptioAiEnabled: payload.deceptioAiEnabled
      });
      break;

    case 'openVideoInformationWindow':
      store.videoInformationWindow.open({
        jobId: payload.jobId
      });
      break;

    case 'openCredentialsExpiredWindow':
      store.credentialsExpiredWindow.open({
        library: payload.library
      });
      break;

    case 'openUploadThumbnailWindow':
      store.uploadThumbnailWindow.open({
        jobId: payload.jobId
      });
      break;

    case 'openTrainerKeyboardShortcutsWindow':
      store.trainerKeyboardShortcutsWindow.open();
      break;

    case 'enableCloseTabWarning':
      if (store.isCloseTabWarningEnabled)
        break;

      window.onbeforeunload = (evt: BeforeUnloadEvent) => {
        evt.preventDefault();
        evt.returnValue = '';
      };

      store.isCloseTabWarningEnabled = true;
      break;

    case 'disableCloseTabWarning':
      window.onbeforeunload = null;
      store.isCloseTabWarningEnabled = false;
      break;

    case 'openVideoDetailsWindow':
      store.videoDetailsWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId,
        layoutType: payload.layoutType || 'partial'
      });
      break;

    case 'openScheduleStreamWindow':
      store.scheduleStreamWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId,
        layoutType: payload.layoutType || 'default'
      });
      break;

    case 'openAssetsWindow':
      store.assetsWindow.open({
        teamId: payload.teamId,
        jobId: payload.jobId
      });
      break;

    case 'openliveFeedWindow':
      store.liveFeedWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId,
      });
      break;

    case 'openLiveNotStreamingWindow':
      store.liveNotStreamingWindow.open({
        jobId: payload.jobId,
        liveStreamActivePlaylist: payload.liveStreamActivePlaylist,
      });
      break;

    case 'openManualIngestWindow':
      store.manualIngestWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId,
      });
      break;

    case 'openStreamQueueSettingsWindow':
      store.streamQueueSettingsWindow.open({
        teamId: payload.teamId,
      });
      break;

    case 'openConfirmationModal':
      store.confirmationModal.open({
        onSubmit: payload.onSubmit,
        onCancel: payload.onCancel,
        onClose: payload.onClose,
        modalMessage: payload.message,
        modalSecondaryMessage: payload.secondaryMessage,
        title: payload.title,
        confirmLabel: payload.confirmLabel,
        closeLabel: payload.closeLabel,
        isLoading: payload.isLoading,
        layout: payload.layout
      });
      break;

    case 'openJobViewStatusWindow':
      store.jobViewStatusWindow.open({
        jobId: payload.jobId,
        teamId: payload.teamId,
        isPublished: payload.isPublished
      });
      break;

    case 'openExternalLibraryUploadWindow':
      store.externalLibraryUploadWindow.open({
        activeTab: payload.activeTab
      });
      break;

    case 'makeTeamPublicWindow':
      store.makeTeamPublicWindow.open({
        teamId: payload.teamId
      });
      break;

    case 'openCancelUploadWindow':
      store.cancelUploadWindow.open({
        task: payload.task
      });
      break;

    case 'openInfoModal':
      store.infoModal.open({
        title: payload.title,
        content: payload.content,
        windowClassName: payload.windowClassName
      });
      break;

    case 'openTeamProductWindow':
      store.teamProductWindow.open({
        team: payload.team
      });
      break;

    case 'openSpeakerIdConfirmationWindow':
      store.speakerIdWindow.open({
        onSubmit: payload.onSubmit,
        onCancel: payload.onCancel,
        onClose: payload.onClose,
        onSuppress: payload.onSuppress,
        targetCount: payload.targetCount,
        jobId: payload.jobId
      });
      break;

    case 'openCancelAccountWindow':
      store.cancelAccountWindow.open();
      break;

    case 'openTeamDictionaryWindow':
      store.teamDictionaryWindow.open(payload.teamId);
      break;

    case 'openEditorWindow':
      store.editorWindow.open({
        jobId: payload.jobId,
        data: payload.data,
        fileName: payload.fileName,
        mode: payload.mode,
        isManualReport: payload.isManualReport,
        aiDraftContent: payload.aiDraftContent,
        editorMode: payload.editorMode,
        readOnly: payload.readOnly,
      });
      break;

    case 'openDeceptioWindow':
      store.deceptioWindow.open({
        iFrameUrl: payload.iFrameUrl
      });
      break;
  }
}
